import React, { useMemo, useRef, useCallback } from "react";
import { Typography, Box, Button, Skeleton } from "@mui/material";
import PurchaseOrder from "./PurchaseOrder";
import { Link } from "react-router-dom";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useSelector } from "react-redux";
import { selectAutoAddLoading } from "features/autoAddSlice";
import RefreshCartsBtn from "../../custom/buttons/RefreshCarts";
import { LOADING_STATES } from "features/cartsSlice";
import TableViewIcon from '@mui/icons-material/TableView';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import html2pdf from 'html2pdf.js';
import { resetScrollFlag } from "features/highlightSlice";
import useScrollToHighlight, { ScrollContext as SharedScrollContext } from './hooks/useScrollToHighlight';

// Create a scroll context
const PurchaseOrdersScrollContext = React.createContext({
  scrollToElement: () => {},
  containerRef: null
});

const exportToPDF = (processedVendorData) => {
  // Get current date in a readable format
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Cart Export</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { display: flex; justify-content: flex-end; margin-bottom: 20px; }
          .date { text-align: right; font-weight: bold; }
          .vendor { margin: 20px 0; page-break-inside: avoid; }
          .invoice { margin: 15px 0; border-top: 1px solid #eee; }
          table { border-collapse: collapse; width: 100%; margin: 10px 0; }
          th { text-align: left; border-bottom: 1px solid #ddd; padding: 8px 4px; }
          td { padding: 8px 4px; }
          .total { text-align: right; font-weight: bold; padding: 8px 4px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="date">${currentDate}</div>
        </div>
        ${processedVendorData
          .map(
            (vendor) => `
          <div class="vendor">
            <h2>${vendor.vendorName}</h2>
            ${vendor.invoices
              .map(
                (invoice) => `
              <div class="invoice">
                <h3>${
                  invoice.invoiceNumber === "Unassigned Items"
                    ? "Unassigned Items"
                    : `Invoice #${invoice.invoiceNumber} ${invoice.invoiceNickname ? `- ${invoice.invoiceNickname}` : ""}`
                }</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Style</th>
                      <th>Brand</th>
                      <th>Color</th>
                      <th>Size</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Warehouse</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${invoice.invoiceItems
                      .map(
                        (item) => `
                    <tr>
                      <td>${item.style}</td>
                      <td>${item.master_brand || ""}</td>
                      <td>${item.master_color}</td>
                      <td>${item.size}</td>
                      <td>${item.quantity}</td>
                      <td>$${(() => {
                        return typeof item.unitPrice === "number"
                          ? item.unitPrice?.toFixed(2)
                          : item.unitPrice;
                      })()}</td>
                      <td>${item.warehouse}</td>
                    </tr>
                  `
                      )
                      .join("")}
                  </tbody>
                </table>
                <div class="total">Total: $${invoice.totalPrice.toFixed(
                  2
                )}</div>
              </div>
            `
              )
              .join("")}
          </div>
        `
          )
          .join("")}
      </body>
    </html>
  `;

  // Create a temporary element to hold the HTML content
  const element = document.createElement('div');
  element.innerHTML = html;
  document.body.appendChild(element);

  // Configure PDF options
  const options = {
    margin: 10,
    filename: 'cart-export.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
  };

  // Generate and download the PDF
  html2pdf().from(element).set(options).save().then(() => {
    // Remove the temporary element after PDF generation
    document.body.removeChild(element);
  });
};

const exportToCSV = (processedVendorData) => {
  // Create CSV header
  let csv =
    "Vendor,Invoice Number,Invoice Nickname,Style,Brand,Color,Size,Quantity,Unit Price,Total Price,Warehouse\n";

  // Add data rows
  processedVendorData.forEach((vendor) => {
    vendor.invoices.forEach((invoice) => {
      invoice.invoiceItems.forEach((item) => {
        csv += `${vendor.vendorName},`;
        csv += `${invoice.invoiceNumber},`;
        csv += `${invoice.invoiceNickname},`;
        csv += `${item.style},`;
        csv += `${item.master_brand || ""},`;
        csv += `${item.master_color},`;
        csv += `${item.size},`;
        csv += `${item.quantity},`;
        csv += `$${
          typeof item.unitPrice === "number"
            ? item.unitPrice.toFixed(2)
            : item.unitPrice
        },`;
        csv += `$${invoice.totalPrice.toFixed(2)},`;
        csv += `${item.warehouse}\n`;
      });
    });
  });

  // Create and trigger download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "cart-export.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ActivePurchaseOrders = () => {
  const allCarts = useSelector((state) => state.carts.carts);
  const vendors = Object.entries(allCarts);
  const isAddingToCarts = useSelector(selectAutoAddLoading);

  // Memoize processedVendorData to prevent unnecessary recalculations
  const processedVendorData = useMemo(() => {
    return vendors.map(([vendorCode, vendorData]) => {
      // Group by invoice (same as before)
      const itemGroupings = (vendorData.items || []).reduce((acc, item) => {
        const invoiceNum = item.invoice_number || "Unassigned Items";
        if (!acc[invoiceNum]) {
          acc[invoiceNum] = {
            items: [],
            nickname: item.invoiceNickname || ""
          };
        }
        acc[invoiceNum].items.push(item);
        return acc;
      }, {});

      const groupedByInvoice = Object.entries(itemGroupings).map(
        ([invoiceNum, group]) => ({
          invoiceNumber: invoiceNum,
          invoiceNickname: group.nickname,
          invoiceItems: group.items,
          totalPrice: group.items.reduce(
            (sum, item) =>
              sum + (item.totalPrice || item.unitPrice * item.quantity || 0),
            0
          ),
        })
      );

      // Check if first item has direct_vendor field
      const isDirectCart = vendorData.items?.[0]?.direct_vendor || false;

      return {
        vendorCode,
        vendorName: vendorData.name,
        freeShippingMin: vendorData.freeShippingMin || 200,
        isDirectCart,
        invoices: groupedByInvoice,
        isLoading: vendorData.loading,
      };
    });
  }, [vendors]);

  const memoizedTypography = useMemo(
    () => <Typography variant="h5" sx={{ m: 0, mr: 2, p: 0 }}>Shopping Carts</Typography>,
    []
  );

  const memoizedButtons = useMemo(
    () => (
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        width: { xs: '100%', sm: 'auto' }
      }}>
        <Button
          sx={{ 
            color: 'black',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: 'grey.600'
            },
            '& .MuiSvgIcon-root': {
              color: 'black'
            },
            '&:hover .MuiSvgIcon-root': {
              color: 'grey.600'
            }
          }}
          className="normal-case"
          variant="text"
          startIcon={<TableViewIcon />}
          onClick={() => exportToCSV(processedVendorData)}
        >
          Export CSV
        </Button>
        <Button
          sx={{ 
            color: 'black',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: 'grey.600'
            },
            '& .MuiSvgIcon-root': {
              color: 'black'
            },
            '&:hover .MuiSvgIcon-root': {
              color: 'grey.600'
            }
          }}
          variant="text"
          className="normal-case"
          startIcon={<PictureAsPdfIcon />}
          onClick={() => exportToPDF(processedVendorData)}
        >
          Export PDF
        </Button>
      </Box>
    ),
    [processedVendorData]
  );

  const memoizedHeaderContent = useMemo(
    () => (
      <Box
        sx={{
          height: '42px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          mb: '0.5rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {memoizedTypography}
          <RefreshCartsBtn />
        </Box>
        
        {memoizedButtons}
      </Box>
    ),
    [memoizedTypography, memoizedButtons]
  );

  // Memoize sx props to prevent them from being recreated on each render
  const memoizedSxMainBox = useMemo(
    () => ({
      width: "100%",
      maxHeight: "calc(100vh - 160px)",
      display: "flex",
      flexDirection: "column",
    }),
    []
  );

  const memoizedSxHeaderBox = useMemo(
    () => ({
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      // marginBottom: "25px",
    }),
    []
  );

  const memoizedSxOverlayBox = useMemo(
    () => ({
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      zIndex: 1000,
      borderRadius: "5px",
    }),
    []
  );

  const allCartsEmpty = processedVendorData.length === 0;
  const allVendorsLoading = processedVendorData.every(vendor => vendor.isLoading === LOADING_STATES.LOADING_STATUS);

  // Use the shared scroll hook but only extract the scrollToElement function
  const { scrollToElement, scrollContainerRef } = useScrollToHighlight(300);

  return (
    <Box sx={{ ...memoizedSxMainBox }}>
      {memoizedHeaderContent}
      
      <PurchaseOrdersScrollContext.Provider value={{ scrollToElement, containerRef: scrollContainerRef }}>
        {allVendorsLoading && !allCartsEmpty ? (
          <Box className="flex flex-col gap-[16px]">
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={48} />
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            sx={{
              flexGrow: 1,
              overflow: "auto", 
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "20px"
            }}
          >
            {processedVendorData.map((vendor, index) => {
              //memeoize the vendor prop to ensure it remains stable across renders
              const memoizedVendor = [
                vendor.vendorCode,
                {
                  name: vendor.vendorName,
                  items: vendor.invoices.flatMap((inv) => inv.invoiceItems),
                  invoices: vendor.invoices,
                  freeShippingMin: vendor.freeShippingMin,
                  isDirectCart: vendor.isDirectCart,
                  isLoading: vendor.isLoading,
                },
              ];

              return (
                <div
                  key={`${vendor.vendorCode}-${index}`}
                  style={{ position: "relative" }}
                >
                  {isAddingToCarts && <Box sx={memoizedSxOverlayBox} />}
                  <PurchaseOrder key={index} vendor={memoizedVendor} />
                </div>
              );
            })}
          </Box>
        )}
      </PurchaseOrdersScrollContext.Provider>
    </Box>
  );
};

const ActivePurchaseOrdersWithContext = ActivePurchaseOrders;
ActivePurchaseOrdersWithContext.ScrollContext = PurchaseOrdersScrollContext;

export default ActivePurchaseOrdersWithContext;
